export class BrowserStorage {
  private readonly storage: Storage;

  constructor(storageType: 'local' | 'session' = 'local') {
    this.storage = storageType === 'local' ? localStorage : sessionStorage;
  }

  /**
   * Sets an item in storage with optional expiration
   * @param key Storage key
   * @param value Value to store
   * @param expirationInMinutes Optional expiration time in minutes
   */
  set<T>(key: string, value: T, expirationInMinutes?: number): void {
    const item = {
      value,
      timestamp: expirationInMinutes ? new Date().getTime() + expirationInMinutes * 60 * 1000 : null,
    };

    try {
      this.storage.setItem(key, JSON.stringify(item));
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.error('Storage quota exceeded. Trying to clear expired items...');
        this.clearExpired();
        // Retry setting the item
        this.storage.setItem(key, JSON.stringify(item));
      } else {
        throw error;
      }
    }
  }

  /**
   * Gets an item from storage
   * @param key Storage key
   * @param defaultValue Optional default value if key doesn't exist
   * @returns The stored value or defaultValue if not found
   */
  get<T>(key: string, defaultValue?: T): T | null {
    const item = this.storage.getItem(key);

    if (!item) return defaultValue ?? null;

    try {
      const parsed = JSON.parse(item);

      // Handle both wrapped and raw values
      const value = typeof parsed === 'object' && parsed !== null && 'value' in parsed ? parsed.value : parsed;
      const timestamp =
        typeof parsed === 'object' && parsed !== null && 'timestamp' in parsed ? parsed.timestamp : null;

      // Check if item has expired
      if (timestamp && new Date().getTime() > timestamp) {
        this.remove(key);
        return defaultValue ?? null;
      }

      return value;
    } catch {
      // If parsing fails, return the raw value
      return item as unknown as T;
    }
  }

  /**
   * Removes an item from storage
   * @param key Storage key
   */
  remove(key: string): void {
    this.storage.removeItem(key);
  }

  /**
   * Clears all items from storage
   */
  clear(): void {
    this.storage.clear();
  }

  /**
   * Checks if a key exists in storage
   * @param key Storage key
   * @returns boolean indicating if key exists
   */
  has(key: string): boolean {
    return this.storage.getItem(key) !== null;
  }

  /**
   * Gets all keys from storage
   * @returns Array of storage keys
   */
  keys(): string[] {
    return Object.keys(this.storage);
  }

  /**
   * Gets the number of items in storage
   * @returns Number of items
   */
  size(): number {
    return this.storage.length;
  }

  /**
   * Clears all expired items from storage
   */
  private clearExpired(): void {
    const keys = this.keys();

    for (const key of keys) {
      const item = this.storage.getItem(key);
      if (!item) continue;

      try {
        const parsed = JSON.parse(item) as { timestamp: number | null };
        if (parsed.timestamp && new Date().getTime() > parsed.timestamp) {
          this.remove(key);
        }
      } catch {
        continue;
      }
    }
  }
}
